import { useEffect, useRef, useState } from "react";

interface IHoverWarningMessage {
  message: string;
  showWhen: boolean; // Condition to show warning (e.g., isDisabled)
  children: React.ReactNode;
  fadeOutDuration?: number; // Duration in ms before fading (default 3000)
  buttonText?: string;
  onButtonClick?: () => void;
}

const HoverWarningMessage = ({
  message,
  showWhen,
  children,
  fadeOutDuration = 3000,
  buttonText,
  onButtonClick,
}: IHoverWarningMessage) => {
  const [showWarning, setShowWarning] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const fadeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showWhen && showWarning) {
      setShowWarning(false);
      setIsFading(false);
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    }
  }, [showWhen, showWarning]);

  const handleMouseEnter = () => {
    if (showWhen) {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
      setIsFading(false);
      setShowWarning(true);
    }
  };

  const handleMouseLeave = () => {
    if (showWhen && showWarning) {
      setIsFading(true);
      fadeTimeoutRef.current = setTimeout(() => {
        setShowWarning(false);
        setIsFading(false);
      }, fadeOutDuration);
    }
  };

  return (
    <div
      className="flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {showWarning && (
        <div
          data-testid="hoverWarningMessage"
          className={`ml-2 flex items-center gap-2 whitespace-nowrap text-xs transition-opacity duration-1000 ${
            isFading ? "opacity-0" : "opacity-100"
          }`}
        >
          {buttonText && onButtonClick && (
            <button
              data-testid="hoverWarningButton"
              onClick={onButtonClick}
              className="rounded bg-red-600 px-2 py-0.5 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
            >
              {buttonText}
            </button>
          )}
          <span className="text-red-600 dark:text-red-400">{message}</span>
        </div>
      )}
    </div>
  );
};

export default HoverWarningMessage;
